import { StandardEvents } from '@shopify/events';

/**
 * Adds the sample product referenced by the selected variant's (or product's)
 * `custom.sample_product` metafield to the cart, then sends the customer to the
 * cart page (same behaviour as the main add-to-cart button).
 *
 * The element is a stable wrapper: when the variant changes, its content is
 * replaced with the server-rendered version for the new variant.
 */
class SampleRequestComponent extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;
    this.addEventListener('click', this.#onClick, { signal });

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /** @param {Event} event */
  #onProductSelect = async (event) => {
    try {
      const { detail } = await /** @type {any} */ (event).promise;
      const html = detail?.html;
      if (!html) return;

      const { newProduct, productId } = detail;
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && String(productId) !== this.dataset.productId) {
        return;
      }

      const next = html.querySelector('sample-request-component');
      if (next) this.innerHTML = next.innerHTML;
    } catch {
      // Variant change failed; keep the current state.
    }
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('.sample-request__button');
    if (button) this.#request(/** @type {HTMLButtonElement} */ (button));
  };

  /** @param {HTMLButtonElement} button */
  async #request(button) {
    const wrapper = /** @type {HTMLElement | null} */ (button.closest('.sample-request'));
    const error = wrapper?.querySelector('.sample-request__error');
    const { variantId, productTitle } = wrapper?.dataset ?? {};
    const errorText = this.dataset.errorText;
    if (!variantId) return;

    button.disabled = true;
    if (error) error.hidden = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: { _sample_for: productTitle } }],
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || errorText);
      }

      window.location.assign(Theme.routes.cart_url || '/cart');
    } catch (err) {
      if (error) {
        error.textContent = err instanceof Error && err.message ? err.message : errorText || '';
        error.hidden = false;
      }
      button.disabled = false;
    }
  }
}

if (!customElements.get('sample-request-component')) {
  customElements.define('sample-request-component', SampleRequestComponent);
}
